import type { ReactNode } from "react";

import hoofdfoto from "@/assets/villa/hoofdfoto_orig.jpg.asset.json";
import poolNightArch from "@/assets/villa/20200719-lounge-1-boog-avond_orig.jpg.asset.json";
import loungeDay from "@/assets/villa/20200719-lounge-dichtbij-dag_orig.jpg.asset.json";
import outdoorKitchenNight from "@/assets/villa/20200719-zicht-op-buitenkeuken-avond-4_orig.jpg.asset.json";
import poolLounge from "@/assets/villa/20200722-lounge_orig.jpg.asset.json";
import poolAngleDay from "@/assets/villa/20200722-vanuit-hoek-naar-lounge-dag-2_orig.jpg.asset.json";
import archDay from "@/assets/villa/20240419-184622_orig.jpg.asset.json";
import outdoorDining from "@/assets/villa/20240614-134837_orig.jpg.asset.json";
import poolDusk from "@/assets/villa/20240614-215924-2_orig.jpg.asset.json";
import albirPromenade from "@/assets/villa/albir-promenade.jpeg.asset.json";
import outdoorKitchenCabinet from "@/assets/villa/20200721-buitenkeuken-kastkant_orig.jpg.asset.json";
import outdoorKitchenGreen from "@/assets/villa/20210729-084915-24494_orig.jpg.asset.json";
import gardenLoungersPalm from "@/assets/villa/20200722-ligzetels-onder-palm_orig.jpg.asset.json";
import gardenFull from "@/assets/villa/20200722-tuin-helemaal_orig.jpg.asset.json";
import gardenBougainvillea from "@/assets/villa/img-20181111-wa0014_orig.jpg.asset.json";
import bathroomGreen from "@/assets/villa/bathroom1.jpg.asset.json";
import heroBranded from "@/assets/villa/hoofdfoto-branded.jpg.asset.json";
import twin2Beds from "@/assets/villa/twin2-beds.jpg.asset.json";
import mainBedroom from "@/assets/villa/main-bedroom.jpg.asset.json";
import secondBedroom from "@/assets/villa/second-bedroom.jpg.asset.json";
import twin1Beds from "@/assets/villa/twin1-beds.jpg.asset.json";
import bathroom1Shower from "@/assets/villa/bathroom1-shower.jpg.asset.json";
import twin2Dresser from "@/assets/villa/twin2-dresser.jpg.asset.json";
import twin2Wardrobe from "@/assets/villa/twin2-wardrobe.jpg.asset.json";
import bathroom2 from "@/assets/villa/bathroom2.jpg.asset.json";
import livingRoom1 from "@/assets/villa/living-room-1.jpg.asset.json";
import livingRoom2 from "@/assets/villa/living-room-2.jpg.asset.json";
import indoorKitchen from "@/assets/villa/indoor-kitchen.jpg.asset.json";
import naya1 from "@/assets/villa/naya-1.jpg.asset.json";
import naya2 from "@/assets/villa/naya-2.jpg.asset.json";
import naya4 from "@/assets/villa/naya-4.jpg.asset.json";
import driveway1 from "@/assets/villa/driveway-1.jpg.asset.json";

export const ALBIR_PROMENADE_IMG = albirPromenade.url;

export type Lang = "en" | "nl" | "fr" | "de";

export const LANGS: { code: Lang; label: string; flag: string; flagCode: string; tagline: string }[] = [
  { code: "en", label: "English", flag: "🇬🇧", flagCode: "gb", tagline: "Continue in English" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱", flagCode: "nl", tagline: "Ga verder in het Nederlands" },
  { code: "de", label: "Deutsch", flag: "🇩🇪", flagCode: "de", tagline: "Auf Deutsch fortfahren" },
  { code: "fr", label: "Français", flag: "🇫🇷", flagCode: "fr", tagline: "Continuer en français" },
];

export const HERO = poolNightArch.url;

export type Testimonial = {
  initials: string;
  name: string;
  country: string;
  countryFlag: string;
  date: string; // ISO YYYY-MM
  /** Language of the original quote (kept for reference / structured data). */
  quoteLang: Lang;
  /** Quote translated into each supported language. */
  quote: Record<Lang, string>;
  /** Original review source (Airbnb, Spain-Holidays, TripAdvisor, etc.). */
  source: string;
  rating: number; // 1-5
};

// Verbatim guest reviews migrated from casamipajobe.com/reviews.html.
// Originals are screenshots from Airbnb / Spain-Holidays / TripAdvisor; text
// was transcribed and lightly trimmed (marked with "…") to fit card length.
// The original-language version is preserved under `quoteLang`; the other
// language entries are faithful translations so each page reads in its own
// language.
export const TESTIMONIALS: Testimonial[] = [
  {
    initials: "S.",
    name: "Samir",
    country: "Netherlands",
    countryFlag: "🇳🇱",
    date: "2022-08",
    quoteLang: "nl",
    source: "Airbnb",
    rating: 5,
    quote: {
      nl: "Het huis wordt op de foto's geen eer aangedaan. Zeer ruim zwembad, een buitendouche, grote tuin om in te luieren… De verhuurders zijn een stel geweldige gastheren/gasvrouw. Zeer snelle en transparante communicatie en erg meedenkend. Patricia en Michel, dank jullie wel voor de fijne tijd en jullie geweldige gastvrijheid!",
      en: "The photos don't do the house justice. A very spacious pool, an outdoor shower, a large garden to lounge in… The owners are wonderful hosts. Very fast, transparent communication and always willing to help. Patricia and Michel, thank you for the lovely time and your fantastic hospitality!",
      fr: "Les photos ne rendent pas justice à la maison. Une piscine très spacieuse, une douche extérieure, un grand jardin pour se prélasser… Les propriétaires sont de formidables hôtes. Communication rapide et transparente, toujours à l'écoute. Patricia et Michel, merci pour ce beau séjour et votre formidable accueil !",
      de: "Die Fotos werden dem Haus nicht gerecht. Ein sehr großzügiger Pool, eine Außendusche, ein großer Garten zum Entspannen… Die Vermieter sind großartige Gastgeber. Sehr schnelle und transparente Kommunikation und immer hilfsbereit. Patricia und Michel, vielen Dank für die schöne Zeit und eure tolle Gastfreundschaft!",
    },
  },
  {
    initials: "F.H.",
    name: "Fiona Harrison",
    country: "United Kingdom",
    countryFlag: "🇬🇧",
    date: "2019-05",
    quoteLang: "en",
    source: "Spain-Holiday",
    rating: 5,
    quote: {
      en: "My family and myself spent a week in this beautiful villa in May. The villa itself is spacious with lots of room for a family of 6, the outside area front and back is picturesque. The pool is perfect for kids of all ages and the outside kitchen is the best feature of all, we didn't cook inside once. Without a doubt this villa caters for everything and gets a 5-star rating from me.",
      nl: "Mijn gezin en ik verbleven in mei een week in deze prachtige villa. De villa zelf is ruim met veel plek voor een gezin van 6, en de buitenruimte voor en achter is schilderachtig. Het zwembad is perfect voor kinderen van alle leeftijden en de buitenkeuken is het allerleukste, we hebben geen enkele keer binnen gekookt. Deze villa heeft echt alles en krijgt van mij een 5-sterrenbeoordeling.",
      fr: "Ma famille et moi avons passé une semaine dans cette magnifique villa en mai. La villa est spacieuse avec beaucoup de place pour une famille de 6, et les espaces extérieurs avant et arrière sont pittoresques. La piscine est parfaite pour les enfants de tous âges et la cuisine extérieure est le meilleur atout, nous n'avons jamais cuisiné à l'intérieur. Sans aucun doute, cette villa propose tout ce qu'il faut et reçoit 5 étoiles de ma part.",
      de: "Meine Familie und ich haben im Mai eine Woche in dieser wunderschönen Villa verbracht. Die Villa selbst ist großzügig mit viel Platz für eine 6-köpfige Familie, der Außenbereich vorne und hinten ist malerisch. Der Pool ist perfekt für Kinder jeden Alters und die Außenküche ist das absolute Highlight, wir haben kein einziges Mal drinnen gekocht. Diese Villa bietet zweifellos alles und bekommt von mir 5 Sterne.",
    },
  },
  {
    initials: "K.P.",
    name: "Kayleigh P. (The Parr's)",
    country: "United Kingdom",
    countryFlag: "🇬🇧",
    date: "2019-09",
    quoteLang: "en",
    source: "Spain-Holiday",
    rating: 5,
    quote: {
      en: "Beautiful in every way. This villa is one of the most beautiful ones we've stayed in in the La Nucia area. Our favourite bit as a family had to be the pool area and outdoor kitchen with everything you need so you don't have to go back inside. The views from the roof terrace are picturesque and the main bedroom bed is the comfiest we've ever slept in. Would highly recommend.",
      nl: "In alle opzichten prachtig. Deze villa is een van de mooiste waar we ooit verbleven in de regio La Nucia. Onze favoriete plek als gezin was de zwembadzone met buitenkeuken, alles wat je nodig hebt, zodat je niet meer naar binnen hoeft. Het uitzicht vanaf het dakterras is schilderachtig en het bed in de hoofdslaapkamer is het meest comfortabele waar we ooit in geslapen hebben. Een absolute aanrader.",
      fr: "Magnifique à tous points de vue. Cette villa est l'une des plus belles où nous ayons séjourné dans la région de La Nucia. Notre coin préféré en famille était la piscine et la cuisine extérieure, tout ce qu'il faut pour ne plus avoir à rentrer. La vue depuis la terrasse sur le toit est pittoresque et le lit de la chambre principale est le plus confortable où nous ayons jamais dormi. Hautement recommandé.",
      de: "In jeder Hinsicht wunderschön. Diese Villa ist eine der schönsten, in denen wir in der Region La Nucia übernachtet haben. Unser Lieblingsplatz als Familie war der Poolbereich mit Außenküche, alles, was man braucht, sodass man nicht mehr hinein muss. Der Blick von der Dachterrasse ist malerisch und das Bett im Hauptschlafzimmer ist das bequemste, in dem wir je geschlafen haben. Absolute Empfehlung.",
    },
  },
  {
    initials: "P&K",
    name: "Philippe en Katrien",
    country: "Belgium",
    countryFlag: "🇧🇪",
    date: "2021-08",
    quoteLang: "nl",
    source: "Spain-Holiday",
    rating: 5,
    quote: {
      nl: "Genieten onder Spaanse zon. Het is als thuiskomen, maar dan wel onder de Spaanse zon, alle benodigdheden aanwezig, rustig gelegen en toch alles dichtbij. Zeer verzorgd, proper afgewerkt.",
      en: "Enjoying under the Spanish sun. It feels like coming home, but under the Spanish sun. Everything you need is there, a peaceful spot and yet everything is close by. Very well kept and beautifully finished.",
      fr: "Profiter sous le soleil espagnol. On se sent comme chez soi, mais sous le soleil espagnol, tout le nécessaire est là, situé au calme et pourtant tout est proche. Très soigné et joliment fini.",
      de: "Genießen unter spanischer Sonne. Es fühlt sich an wie nach Hause kommen, nur unter spanischer Sonne, alles Nötige ist vorhanden, ruhig gelegen und doch alles in der Nähe. Sehr gepflegt und sauber verarbeitet.",
    },
  },
  {
    initials: "R.",
    name: "Ronald met gezin",
    country: "Netherlands",
    countryFlag: "🇳🇱",
    date: "2020-08",
    quoteLang: "nl",
    source: "Spain-Holiday",
    rating: 5,
    quote: {
      nl: "Perfecte villa en service. Het is een mooi huis voorzien van alle gemakken. Men doet net dat stapje extra, dat is erg fijn! Communicatie en ontvangst ook super. Ondanks Covid-19 alles goed verlopen en erg meedenkend. Het is niet de vraag of, maar wanneer we terug zullen komen.",
      en: "Perfect villa and service. It's a beautiful house with every comfort. They go that extra mile, which is really nice! Communication and welcome also great. Despite Covid-19 everything went smoothly and they were very accommodating. It's not a question of if, but when we'll be back.",
      fr: "Villa et service parfaits. C'est une belle maison équipée de tout le confort. Ils en font toujours un peu plus, c'est très appréciable ! Communication et accueil également au top. Malgré le Covid-19, tout s'est très bien passé et ils ont fait preuve d'une grande souplesse. Ce n'est pas une question de si, mais de quand nous reviendrons.",
      de: "Perfekte Villa und Service. Es ist ein schönes Haus mit allem Komfort. Man geht immer den Extra-Schritt, das ist sehr angenehm! Kommunikation und Empfang ebenfalls super. Trotz Covid-19 ist alles reibungslos verlaufen und man war sehr entgegenkommend. Es ist keine Frage ob, sondern wann wir zurückkommen.",
    },
  },
  {
    initials: "D&B",
    name: "Dirk en Brigitte Gregoir",
    country: "Belgium",
    countryFlag: "🇧🇪",
    date: "2019-12",
    quoteLang: "nl",
    source: "Spain-Holiday",
    rating: 5,
    quote: {
      nl: "Voor het derde jaar op rij een maand verbleven in Casa Mi Pajobe. Waar het goed is kom je immers terug. Elk jaar zien we aanpassingen aan de villa die de eigenaars gedaan hebben om wat goed was nog beter te maken. Prima contact gehad met Patricia en Michel, die elke vraag en/of probleempje binnen de kortste keren oplossen.",
      en: "For the third year in a row we spent a month at Casa Mi Pajobe. You always return where it's good. Every year we notice improvements the owners have made to make what was already good even better. Excellent contact with Patricia and Michel, who solve every question or small problem in no time.",
      fr: "Pour la troisième année consécutive, nous avons passé un mois à Casa Mi Pajobe. On revient toujours là où l'on se sent bien. Chaque année, nous remarquons les améliorations apportées par les propriétaires pour rendre encore meilleur ce qui était déjà bon. Excellent contact avec Patricia et Michel, qui règlent chaque question ou petit souci en un rien de temps.",
      de: "Zum dritten Mal in Folge haben wir einen Monat in Casa Mi Pajobe verbracht. Wo es gut ist, kommt man zurück. Jedes Jahr sehen wir Verbesserungen, die die Eigentümer vorgenommen haben, um das schon Gute noch besser zu machen. Toller Kontakt mit Patricia und Michel, die jede Frage oder jedes kleine Problem im Handumdrehen lösen.",
    },
  },
  {
    initials: "E.N.",
    name: "Erling Nilsen",
    country: "Norway",
    countryFlag: "🇳🇴",
    date: "2019-08",
    quoteLang: "en",
    source: "Spain-Holiday",
    rating: 4,
    quote: {
      en: "Really nice garden. We had a really nice stay, the pool area is perfect. We had a little problem with hot water, but the owner helped me solve that in minutes; everyone from the keyholder to the owner was really quick to respond. On the beds there were nicely tied packages of towels and small gifts. We felt really welcome.",
      nl: "Een hele mooie tuin. We hadden een heerlijk verblijf, het zwembad is perfect. We hadden een klein probleempje met warm water, maar de eigenaar hielp me dat binnen enkele minuten op te lossen; iedereen, van de sleutelbeheerder tot de eigenaar, reageerde heel snel. Op de bedden lagen mooi ingepakte handdoeken en kleine cadeautjes. We voelden ons echt welkom.",
      fr: "Un très joli jardin. Nous avons passé un super séjour, l'espace piscine est parfait. Nous avons eu un petit problème d'eau chaude, mais le propriétaire l'a résolu en quelques minutes ; tout le monde, du gardien des clés au propriétaire, a réagi très vite. Sur les lits, il y avait de jolis paquets de serviettes et de petits cadeaux. Nous nous sommes sentis vraiment les bienvenus.",
      de: "Wirklich schöner Garten. Wir hatten einen tollen Aufenthalt, der Poolbereich ist perfekt. Wir hatten ein kleines Problem mit dem Warmwasser, aber der Eigentümer hat es in wenigen Minuten gelöst; vom Schlüsselverwalter bis zum Eigentümer reagierten alle sehr schnell. Auf den Betten lagen hübsch verpackte Handtücher und kleine Geschenke. Wir haben uns wirklich willkommen gefühlt.",
    },
  },
  {
    initials: "L.V.",
    name: "Lotte Verwimp",
    country: "Belgium",
    countryFlag: "🇧🇪",
    date: "2018-10",
    quoteLang: "nl",
    source: "Spain-Holiday",
    rating: 5,
    quote: {
      nl: "Een vakantie om te koesteren. De eigenaars van Casa Mi Pajobe verzorgen hun woning tot in de puntjes. Je kan genieten aan het zwembad, dorpjes in de buurt ontdekken, lekker uit eten of genieten van een bbq aan het zwembad. Casa Mi Pajobe is een aanrader!",
      en: "A holiday to cherish. The owners of Casa Mi Pajobe look after their home down to the last detail. You can relax by the pool, explore nearby villages, eat out or enjoy a barbecue by the pool. Casa Mi Pajobe is a must!",
      fr: "Des vacances à chérir. Les propriétaires de Casa Mi Pajobe soignent leur maison dans les moindres détails. Vous pouvez vous détendre au bord de la piscine, découvrir les villages voisins, sortir au restaurant ou profiter d'un barbecue près de la piscine. Casa Mi Pajobe est à recommander !",
      de: "Ein Urlaub zum Genießen. Die Eigentümer von Casa Mi Pajobe pflegen ihr Haus bis ins kleinste Detail. Man kann am Pool entspannen, nahe Dörfer entdecken, schön essen gehen oder ein BBQ am Pool genießen. Casa Mi Pajobe ist absolut empfehlenswert!",
    },
  },
  {
    initials: "S.",
    name: "Said",
    country: "Netherlands",
    countryFlag: "🇳🇱",
    date: "2025-08",
    quoteLang: "nl",
    source: "Micazu",
    rating: 5,
    quote: {
      nl: "Top. We hebben enorm genoten van ons verblijf in Casa Mi Pajobe. Alles was aanwezig, het zwembad was top en de buitenkeuken eveneens. We werden goed ontvangen en de communicatie verliep heel vlot. We komen zeker terug.",
      en: "Top. We thoroughly enjoyed our stay at Casa Mi Pajobe. Everything was there, the pool was top notch and so was the outdoor kitchen. We were warmly welcomed and communication went very smoothly. We will definitely come back.",
      fr: "Au top. Nous avons énormément apprécié notre séjour à Casa Mi Pajobe. Tout était présent, la piscine était parfaite, tout comme la cuisine extérieure. Nous avons été très bien accueillis et la communication a été très fluide. Nous reviendrons assurément.",
      de: "Spitze. Wir haben unseren Aufenthalt im Casa Mi Pajobe sehr genossen. Alles war vorhanden, der Pool war erstklassig und die Außenküche ebenfalls. Wir wurden herzlich empfangen und die Kommunikation lief sehr reibungslos. Wir kommen bestimmt wieder.",
    },
  },
  {
    initials: "B.",
    name: "Brenda",
    country: "Netherlands",
    countryFlag: "🇳🇱",
    date: "2024-09",
    quoteLang: "nl",
    source: "Micazu",
    rating: 4,
    quote: {
      nl: "Eigenaars zijn toppers. Helpen je meteen, heel attent. De villa is prachtig en een heel goede uitvalsbasis. Een auto heb je wel nodig.",
      en: "The owners are wonderful, they help you straight away and are very thoughtful. The villa is beautiful and a great base. You do need a car.",
      fr: "Les propriétaires sont formidables, ils vous aident immédiatement et sont très attentionnés. La villa est magnifique et constitue une excellente base. Il faut prévoir une voiture.",
      de: "Die Eigentümer sind großartig, sie helfen sofort und sind sehr aufmerksam. Die Villa ist wunderschön und ein toller Ausgangspunkt. Ein Auto braucht man allerdings.",
    },
  },
  {
    initials: "J.",
    name: "Joyce",
    country: "Netherlands",
    countryFlag: "🇳🇱",
    date: "2024-06",
    quoteLang: "nl",
    source: "Micazu",
    rating: 4,
    quote: {
      nl: "We hebben dit huis voor het eerst geboekt en hebben er echt van genoten. Het is netjes en van alles voorzien. De buitenruimte met zwembad en keuken is voor mij echt het pronkstuk. We zouden zeker terugkomen en ook de ontvangst was top.",
      en: "We booked this house for the first time and really enjoyed it. It's tidy and equipped with everything. The outdoor area with pool and kitchen is the standout for me. We'd love to come back, and the welcome was top notch too.",
      fr: "Nous avons réservé cette maison pour la première fois et nous avons vraiment adoré. Tout est soigné et bien équipé. L'espace extérieur avec piscine et cuisine est selon moi le point fort. Nous reviendrions volontiers, et l'accueil était également excellent.",
      de: "Wir haben dieses Haus zum ersten Mal gebucht und es wirklich genossen. Es ist gepflegt und mit allem ausgestattet. Der Außenbereich mit Pool und Küche ist für mich das Highlight. Wir würden gerne wiederkommen, auch der Empfang war erstklassig.",
    },
  },
];

export const REVIEW_AGGREGATE = {
  ratingValue: 4.8,
  reviewCount: TESTIMONIALS.length,
};

// Verbatim rates from casamipajobe.com/calendar-and-prices.html (image:
// prijzen-e-20260526). Prices are weekly in EUR per bedroom configuration.
export type RateRow = {
  period: string; // e.g. "01.06.2026 – 10.07.2026"
  // weekly price per bedroom config; null when not available (e.g. peak season
  // when smaller configs aren't bookable)
  beds2: number | null;
  beds3: number | null;
  beds4: number | null;
  // m³ water included per week per bedroom config; null/undefined => 0 included
  water2?: number | null;
  water3?: number | null;
  water4?: number | null;
};

export const RATES: RateRow[] = [
  { period: "01.06.2026 – 11.07.2026", beds2: 910,  beds3: 1050, beds4: 1225, water2: 4.5, water3: 5.5, water4: 6.5 },
  { period: "11.07.2026 – 28.08.2026", beds2: null, beds3: 1595, beds4: 1795, water2: null, water3: 6.5, water4: 7.5 },
  { period: "29.08.2026 – 03.10.2026", beds2: 1050, beds3: 1190, beds4: 1365, water2: 4.5, water3: 5.5, water4: 6.5 },
  { period: "04.10.2026 – 07.11.2026", beds2: 840,  beds3: 980,  beds4: 1155, water2: 4,   water3: 5,   water4: 6 },
  { period: "08.11.2026 – 17.12.2026", beds2: 700,  beds3: 840,  beds4: 1015, water2: 3.5, water3: 4.5, water4: 5.5 },
  { period: "18.12.2026 – 03.01.2027", beds2: 840,  beds3: 980,  beds4: 1155, water2: 3,   water3: 4,   water4: 5 },
];

export type GalleryCategory =
  | "pool"
  | "garden"
  | "outdoor_kitchen"
  | "naya"
  | "bedrooms"
  | "bathrooms"
  | "living_room"
  | "kitchen"
  | "driveway";

export type GalleryItem = {
  src?: string;
  alt: string;
  category: GalleryCategory;
  sub?: GallerySub;
  placeholder?: boolean;
};

export type GallerySub =
  | "main_bedroom"
  | "second_bedroom"
  | "twin_bedroom_1"
  | "twin_bedroom_2"
  | "bathroom_1"
  | "bathroom_2";

export const GALLERY_SUBS: Record<"bedrooms" | "bathrooms", GallerySub[]> = {
  bedrooms: ["main_bedroom", "second_bedroom", "twin_bedroom_1", "twin_bedroom_2"],
  bathrooms: ["bathroom_1", "bathroom_2"],
};

export const GALLERY_SUB_LABELS: Record<"en" | "nl" | "fr" | "de", Record<GallerySub, string>> = {
  en: {
    main_bedroom: "Main bedroom",
    second_bedroom: "Second bedroom",
    twin_bedroom_1: "Twin bedroom 1",
    twin_bedroom_2: "Twin bedroom 2",
    bathroom_1: "Bathroom 1",
    bathroom_2: "Bathroom 2",
  },
  nl: {
    main_bedroom: "Hoofdslaapkamer",
    second_bedroom: "Tweede slaapkamer",
    twin_bedroom_1: "Tweepersoonskamer 1",
    twin_bedroom_2: "Tweepersoonskamer 2",
    bathroom_1: "Badkamer 1",
    bathroom_2: "Badkamer 2",
  },
  fr: {
    main_bedroom: "Chambre principale",
    second_bedroom: "Deuxième chambre",
    twin_bedroom_1: "Chambre twin 1",
    twin_bedroom_2: "Chambre twin 2",
    bathroom_1: "Salle de bain 1",
    bathroom_2: "Salle de bain 2",
  },
  de: {
    main_bedroom: "Hauptschlafzimmer",
    second_bedroom: "Zweites Schlafzimmer",
    twin_bedroom_1: "Zweibettzimmer 1",
    twin_bedroom_2: "Zweibettzimmer 2",
    bathroom_1: "Badezimmer 1",
    bathroom_2: "Badezimmer 2",
  },
};

export const GALLERY: GalleryItem[] = [
  // Pool
  { src: poolNightArch.url, alt: "Pool terrace lit up in the evening", category: "pool" },
  { src: poolAngleDay.url, alt: "Private 8x4m pool with walk-in steps, Costa Blanca", category: "pool" },
  { src: loungeDay.url, alt: "Shaded lounge area beside the pool", category: "pool" },
  { src: poolLounge.url, alt: "Pool and pergola lounge in full sun", category: "pool" },
  { src: poolDusk.url, alt: "Pool at dusk with ambient terrace lighting", category: "pool" },
  { src: archDay.url, alt: "Pool framed by the terrace arch", category: "pool" },
  // Garden
  { src: gardenBougainvillea.url, alt: "Bougainvillea in bloom over the garden steps", category: "garden" },
  { src: gardenLoungersPalm.url, alt: "Sun loungers under the palm tree in the garden", category: "garden" },
  { src: gardenFull.url, alt: "Mediterranean garden with citrus trees and mountain views", category: "garden" },
  // Outdoor kitchen
  { src: outdoorKitchenNight.url, alt: "Fully equipped outdoor kitchen with gas hob, fridge and dishwasher, evening", category: "outdoor_kitchen" },
  { src: outdoorKitchenCabinet.url, alt: "Outdoor kitchen with wood-fired brick oven and mosaic dining table", category: "outdoor_kitchen" },
  { src: outdoorKitchenGreen.url, alt: "Green-tiled outdoor kitchen with brick BBQ and large dining table", category: "outdoor_kitchen" },
  // Naya (covered terrace)
  { src: naya1.url, alt: "Naya dining area with arched windows, wooden dining table and garden views", category: "naya" },
  { src: naya2.url, alt: "Naya with long wooden dining table, chess set and view through arched windows to the palm garden", category: "naya" },
  { src: naya4.url, alt: "Naya arched windows looking out over palms and Mediterranean garden", category: "naya" },
  // Bedrooms
  { src: mainBedroom.url, alt: "Main bedroom with king-size bed, carved wood panels and terrace access", category: "bedrooms", sub: "main_bedroom" },
  { src: secondBedroom.url, alt: "Second bedroom with turquoise accent wall, double bed and ocean-themed decor", category: "bedrooms", sub: "second_bedroom" },
  { src: twin1Beds.url, alt: "Twin bedroom 1 with peacock-themed bedding, blue accent wall and twin beds", category: "bedrooms", sub: "twin_bedroom_1" },
  { src: twin2Beds.url, alt: "Twin bedroom 2 with carved wood headboard, twin beds and garden window", category: "bedrooms", sub: "twin_bedroom_2" },
  { src: twin2Dresser.url, alt: "Twin bedroom 2 with traditional wood dresser, bookshelves and air conditioning", category: "bedrooms", sub: "twin_bedroom_2" },
  { src: twin2Wardrobe.url, alt: "Twin bedroom 2 angled view with built-in wardrobe and twin beds", category: "bedrooms", sub: "twin_bedroom_2" },
  // Bathrooms
  { src: bathroomGreen.url, alt: "Guest bathroom with green tiled walls, walk-in shower and LED mirror", category: "bathrooms", sub: "bathroom_1" },
  { src: bathroom1Shower.url, alt: "Bathroom 1 walk-in shower with arched opening, rainfall shower and green feature wall", category: "bathrooms", sub: "bathroom_1" },
  { src: bathroom2.url, alt: "Bathroom 2 with green subway tiled walls, bathtub, walk-in shower and black-framed fixtures", category: "bathrooms", sub: "bathroom_2" },
  // Living room
  { src: livingRoom1.url, alt: "Living room with blue sofas, pink accent cushions and arched doorway to the naya", category: "living_room" },
  { src: livingRoom2.url, alt: "Living room and dining area with carved wood furniture and open kitchen pass-through", category: "living_room" },
  // Indoor kitchen
  { src: indoorKitchen.url, alt: "Indoor kitchen with white cabinets, dark countertops, glossy green tiled backsplash and full appliances", category: "kitchen" },
  // Driveway
  { src: driveway1.url, alt: "Private gated driveway with patterned paving, Mediterranean front garden and villa entrance", category: "driveway" },
];

export const GALLERY_CATEGORY_LABELS: Record<"en" | "nl" | "fr" | "de", Record<GalleryCategory | "all", string>> = {
  en: { all: "All", pool: "Pool", garden: "Garden", outdoor_kitchen: "Outdoor kitchen", naya: "Naya", bedrooms: "Bedrooms", bathrooms: "Bathrooms", living_room: "Living room", kitchen: "Indoor kitchen", driveway: "Driveway" },
  nl: { all: "Alle", pool: "Zwembad", garden: "Tuin", outdoor_kitchen: "Buitenkeuken", naya: "Naya", bedrooms: "Slaapkamers", bathrooms: "Badkamers", living_room: "Woonkamer", kitchen: "Keuken", driveway: "Oprit" },
  fr: { all: "Tout", pool: "Piscine", garden: "Jardin", outdoor_kitchen: "Cuisine extérieure", naya: "Naya", bedrooms: "Chambres", bathrooms: "Salles de bain", living_room: "Salon", kitchen: "Cuisine", driveway: "Allée" },
  de: { all: "Alle", pool: "Pool", garden: "Garten", outdoor_kitchen: "Außenküche", naya: "Naya", bedrooms: "Schlafzimmer", bathrooms: "Badezimmer", living_room: "Wohnzimmer", kitchen: "Küche", driveway: "Einfahrt" },
};

export const GALLERY_EMPTY_LABEL: Record<"en" | "nl" | "fr" | "de", string> = {
  en: "Photos coming soon.",
  nl: "Foto's binnenkort beschikbaar.",
  fr: "Photos bientôt disponibles.",
  de: "Fotos folgen in Kürze.",
};

export const GALLERY_SECTION_LABELS: Record<
  "en" | "nl" | "fr" | "de",
  { outdoor: string; indoor: string; comingSoon: string }
> = {
  en: { outdoor: "Outdoor spaces", indoor: "Indoor spaces", comingSoon: "Photo coming soon" },
  nl: { outdoor: "Buitenruimtes", indoor: "Binnenruimtes", comingSoon: "Foto binnenkort" },
  fr: { outdoor: "Espaces extérieurs", indoor: "Espaces intérieurs", comingSoon: "Photo à venir" },
  de: { outdoor: "Außenbereiche", indoor: "Innenbereiche", comingSoon: "Foto folgt in Kürze" },
};

export type IndoorRoom = {
  key:
    | "main_bedroom"
    | "second_bedroom"
    | "twin_bedroom_1"
    | "twin_bedroom_2"
    | "bathroom_1"
    | "bathroom_2"
    | "living_room"
    | "kitchen";
  alt: string;
};

export const INDOOR_PLACEHOLDERS: IndoorRoom[] = [
  { key: "main_bedroom", alt: "Main bedroom" },
  { key: "second_bedroom", alt: "Second bedroom" },
  { key: "twin_bedroom_1", alt: "Twin bedroom 1" },
  { key: "twin_bedroom_2", alt: "Twin bedroom 2" },
  { key: "bathroom_1", alt: "Bathroom 1" },
  { key: "bathroom_2", alt: "Bathroom 2" },
  { key: "living_room", alt: "Living room" },
  { key: "kitchen", alt: "Kitchen / indoor dining" },
];

export type TStrings = {
  navVilla: string; navGallery: string; navReviews: string; navLocation: string; navRates: string;
  navGuide: string; navFaq: string;
  bookCta: string; heroEyebrow: string; heroSub: string; heroPrimary: string; heroSecondary: string;
  heroQuote: string; heroQuoteAttribution: string;
  aboutEyebrow: string; aboutTitle: string; aboutP1: string; aboutP2: string; aboutP3: string;
  glance: {
    eyebrow: string;
    tiles: { title: string; sub: string }[];
    rulesTitle: string;
    rules: string[];
  };
  featuresEyebrow: string; featuresTitle: string;
  features: { title: string; body: string }[];
  testimonialsEyebrow: string; testimonialsTitle: string; testimonialsLead: string;
  testimonialsStayed: string;
  galleryEyebrow: string; galleryTitle: string; galleryLead: string;
  locationEyebrow: string; locationTitle: string; locationLead: string;
  locationItems: [string, string][];
  surroundingsEyebrow: string; surroundingsTitle: string; surroundingsBody: string[];
  pricingEyebrow: string; pricingTitle: string;
  pricingLeft: string[]; pricingRight: string[];
  ratesIntro: string;
  ratesColPeriod: string;
  ratesPerWeek: string;
  ratesBedroomsLabel: (n: number) => string;
  ratesNotAvailable: string;
  ratesIncludedTitle: string;
  ratesNotIncludedTitle: string;
  ratesIncluded: string[];
  ratesNotIncluded: string[];
  ratesStayInfoTitle: string;
  ratesHouseRulesTitle: string;
  faqEyebrow: string; faqTitle: string; faqLead: string;
  faq: { q: string; a: string }[];
  ctaTitle: string; ctaBody: string; ctaButton: string;
  footerExplore: string; footerContact: string;
  footerContactBody: (link: ReactNode) => ReactNode;
  footerLicense: string; changeLanguage: string;
};

export const T: Record<Lang, TStrings> = {
  en: {
    navVilla: "The villa", navGallery: "Gallery", navReviews: "Reviews", navLocation: "Location", navRates: "Rates", navGuide: "Guide", navFaq: "FAQ",
    bookCta: "Book", heroEyebrow: "LA NUCIA · COSTA BLANCA ·\nVT-454687-A\u00a0\n",
    heroSub: "A detached villa with a private pool, large outdoor kitchen and enclosed Mediterranean garden. Your dream holiday on the Costa Blanca starts here.",
    heroPrimary: "Book now", heroSecondary: "View the villa",
    heroQuote: "For the third year running, we stayed a full month. Every year we see improvements the owners have made.",
    heroQuoteAttribution: "Dirk & Brigitte, Belgium · Spain-Holiday",
    aboutEyebrow: "The villa", aboutTitle: "Quiet, private, and built for long lazy days.",
    aboutP1: "Casa Mi Pajobe is a detached villa set on 1000 m² of enclosed grounds in the Panorama urbanisation of La Nucia, just 5 km from Benidorm and the beaches of the Costa Blanca.",
    aboutP2: "Four bedrooms, two renovated bathrooms, a bright living room opening onto the garden room and outside, a private 8x4m pool with walk-in steps, a lounge and the fully equipped outdoor kitchen our guests fall in love with.",
    aboutP3: "You are always the only tenant. The whole plot is fenced and closed by an electric gate, with parking for several cars.",
    glance: {
      eyebrow: "At a glance",
      tiles: [
        { title: "Up to 8 guests", sub: "4 bedrooms" },
        { title: "2 bathrooms", sub: "1 with bath, shower & WC · 1 with walk-in shower & WC" },
        { title: "Private pool", sub: "8 × 4 m · depth 1–2 m · walk-in steps · full privacy" },
        { title: "Baby pack available", sub: "Travel cot + high chair + linen · 0–24 months · max. 2 babies" },
        { title: "Air conditioning", sub: "Hot & cold in living room and all bedrooms" },
        { title: "Washer & dryer", sub: "In separate utility room" },
        { title: "Free Wi-Fi", sub: "Fibre optic network" },
        { title: "Private parking", sub: "Multiple cars · enclosed grounds · electric gate · 1 carport" },
        { title: "Check-in from 16:00", sub: "Check-out by 10:00" },
        { title: "Airports nearby", sub: "Alicante 59 km (±35 min) · Valencia 98 km (±60 min)" },
      ],
      rulesTitle: "House rules",
      rules: ["No smoking", "No pets", "No parties or loud music"],
    },
    featuresEyebrow: "What's inside", featuresTitle: "Everything for an easy stay.",
    features: [
      { title: "Private 8x4m pool", body: "Sun-drenched all day, walk-in steps, surrounded by a sheltered terrace with sun beds and parasols." },
      { title: "Large outdoor kitchen", body: "Our guests' favourite spot, 4-burner gas hob, fridge/freezer, dishwasher and a long dining table." },
      { title: "4 bedrooms · sleeps 8", body: "King-size, double and two twin rooms. Two recently renovated bathrooms. Air-con throughout." },
      { title: "1000 m² fenced grounds", body: "Electric gate, parking for several cars, enclosed garden, total privacy for families and friends." },
      { title: "5 km from Benidorm beaches", body: "La Nucia, Costa Blanca. Shops and restaurants within walking distance, motorway exit at 3 km." },
      { title: "All-season comfort", body: "Fixed hot/cold air conditioning in bedrooms and living room, plus gas central heating for winter stays." },
    ],
    testimonialsEyebrow: "Guest reviews",
    testimonialsTitle: "What guests say after their stay",
    testimonialsLead: "A few words from families who've spent their holiday at Casa Mi Pajobe.",
    testimonialsStayed: "Stayed",
    galleryEyebrow: "Gallery", galleryTitle: "Explore the villa",
    galleryLead: "Take a closer look at Casa Mi Pajobe, four bright bedrooms, two renovated bathrooms, a spacious living room and a Mediterranean garden built for easy days.",
    locationEyebrow: "Location", locationTitle: "La Nucia, Costa Blanca",
    locationLead: "The villa is in the quiet Panorama urbanisation, with views over the mountains and the Benidorm skyline. Shops and restaurants are within walking distance, the motorway exit is at 3 km.",
    locationItems: [
      ["Benidorm & beach", "5 km"], ["Albir", "10 min"], ["Shopping La Marina", "5 km"],
      ["Alicante airport", "59 km"], ["Valencia airport", "98 km"], ["Motorway exit", "3 km"],
    ],
    surroundingsEyebrow: "Around the villa",
    surroundingsTitle: "Surroundings & activities",
    surroundingsBody: [
      "Casa Mi Pajobe sits in La Nucia, a relaxed hillside village that keeps its Spanish character while Benidorm is only 5 km away. You get the quiet of the mountains in the evening and the buzz of the coast whenever you feel like it, without ever needing a long drive.",
      "The nearest beaches are a short ride down the hill. Playa del Albir has a calm pebble bay and a car-free promenade, while Benidorm's Playa de Poniente and Levante offer wide stretches of sand, beach bars and water sports. Families with children on the Costa Blanca will find plenty close by: Terra Mítica, Aqualandia, Mundomar and the Terra Natura animal park are all within twenty minutes, and La Nucia's own sports city has pools, tennis courts and a skate park.",
      "Day trips are easy from here. Walk the Bernia ridge or the Fonts de l'Algar waterfalls, drive up to the old town of Guadalest above its turquoise reservoir, or head north to Altea's whitewashed streets and Calpe's Peñón de Ifach. Alicante and Valencia are both an easy drive for a city day.",
      "Closer to home, La Nucia has a weekly market, a good spread of tapas bars and family restaurants, and supermarkets within walking distance of the villa, so most evenings you will simply fire up the outdoor kitchen and stay by the pool.",
    ],
    pricingEyebrow: "Booking", pricingTitle: "Stay & rates",
    pricingLeft: [
      "Minimum stay: 7 nights",
      "Arrival/departure any day, July & August Saturday to Saturday",
      "Check-in 4–8 p.m. · Check-out 8–10 a.m.",
      "Deposit on reservation: 25% of the rental price",
      "Balance, towels, cleaning & security deposit: 8 weeks before arrival",
    ],
    pricingRight: [
      "Non-smoking villa", "No pets",
      "Tourist rentals only, no groups of young people, no parties",
    ],
    ratesIntro: "Weekly rates in EUR. Book fewer bedrooms and pay less, the rest of the villa is fully yours.",
    ratesColPeriod: "Period",
    ratesPerWeek: "/ week",
    ratesBedroomsLabel: (n) => `${n} bedrooms`,
    ratesNotAvailable: "-",
    ratesIncludedTitle: "Included",
    ratesNotIncludedTitle: "Not included",
    ratesIncluded: [
      "Check-in / check-out by our keyholder",
      "Beds prepared on arrival",
      "Wi-Fi and TV (Flanders)",
      "Water consumption (period & occupancy dependent, extra €4.00/m³)",
    ],
    ratesNotIncluded: [
      "Cleaning + linen wash: €230 per stay",
      "Bed, bath & kitchen linen: €10 per person/stay",
      "Security deposit: €500 (refunded after stay)",
      "Baby package (0–24 months): €30 per stay",
      "Electricity: €0.40 / kWh (deducted from deposit)",
      "Gas in low season (deducted from deposit)",
    ],
    ratesStayInfoTitle: "Booking conditions",
    ratesHouseRulesTitle: "House rules",
    faqEyebrow: "FAQ",
    faqTitle: "Frequently asked questions",
    faqLead: "Quick answers about check-in, check-out, cancellations and pets.",
    faq: [
      { q: "What time is check-in?", a: "Check-in is between 4 p.m. and 8 p.m. Our keyholder will meet you at the villa, hand over the keys and show you around. If your flight arrives outside these hours, let us know in advance and we'll do our best to arrange it." },
      { q: "What time is check-out?", a: "Check-out is between 8 a.m. and 10 a.m. on the day of departure, so the villa can be cleaned and prepared for the next guests." },
      { q: "What is the cancellation policy?", a: "The 25% deposit paid at booking is non-refundable. The balance is due 8 weeks before arrival; cancellations after that date are non-refundable. We strongly recommend travel insurance with cancellation cover." },
      { q: "Are pets allowed?", a: "No, pets are not allowed at Casa Mi Pajobe. The villa is also strictly non-smoking indoors." },
      { q: "Is linen included?", a: "Bed, bath and kitchen linen is available for €10 per person per stay. Towels are included in the balance payment." },
      { q: "Is there Wi-Fi?", a: "Yes, fast Wi-Fi is available throughout the villa and garden." },
      { q: "Can we bring a baby or toddler?", a: "Yes. A travel cot and high chair are available for €30 (included in the baby pack on the booking form)." },
      { q: "Is the pool heated?", a: "The pool is not heated but receives direct sun all day and is comfortably warm from May through October." },
      { q: "What is the nearest supermarket?", a: "There is a Mercadona supermarket within walking distance in La Nucia, approximately 10 minutes on foot." },
      { q: "Is there parking at the villa?", a: "Yes, the enclosed grounds have parking for several cars behind the electric gate." },
    ],
    ctaTitle: "Ready to plan your stay?",
    ctaBody: "Send a quick inquiry, we reply within 24 hours.",
    ctaButton: "Start booking inquiry",
    footerExplore: "Explore", footerContact: "Contact",
    footerContactBody: (link) => <>For any question, price quote or reservation, please use our {link}.</>,
    footerLicense: "Verhuur licentie\u00a0 VT-454687-A\u00a0\nVERGUNNING KORTE TERMIJN VERHUUR:\u00a0ESFCTU00000305300037183700000000000000000VT-454687-A3\u00a0\nVERGUNNING LANGE TERMIJN VERHUUR: ESFCNT000003053000371837000000000000000000000000009", changeLanguage: "Change language",
  },
  nl: {
    navVilla: "De villa", navGallery: "Foto's", navReviews: "Beoordelingen", navLocation: "Ligging", navRates: "Tarieven", navGuide: "Gids", navFaq: "Veelgestelde vragen",
    bookCta: "Reserveren", heroEyebrow: "LA NUCIA · COSTA BLANCA ·\nVT-454687-A\u00a0\n",
    heroSub: "Een vrijstaande villa met privézwembad, grote buitenkeuken en omsloten mediterrane tuin. Uw droomvakantie aan de Costa Blanca begint hier.",
    heroPrimary: "Boek nu", heroSecondary: "Bekijk de villa",
    heroQuote: "Voor het derde jaar op rij verbleven we een volledige maand. Elk jaar zien we verbeteringen die de eigenaars hebben aangebracht.",
    heroQuoteAttribution: "Dirk & Brigitte, België · Spain-Holiday",
    aboutEyebrow: "De villa", aboutTitle: "Rustig, privé en gemaakt voor lange luie dagen.",
    aboutP1: "Casa Mi Pajobe is een vrijstaande villa op 1000 m² omsloten terrein in de urbanisatie Panorama in La Nucia, slechts 5 km van Benidorm en de stranden van de Costa Blanca.",
    aboutP2: "Vier slaapkamers, twee gerenoveerde badkamers, een lichtrijke woonkamer met aansluitende naya en buiten een privézwembad van 8m op 4m met instaptrap, een lounge en de volledig uitgeruste buitenkeuken waar onze gasten dol op zijn.",
    aboutP3: "U bent altijd de enige huurder. Het hele perceel is omheind en afgesloten met een elektrische poort, met parkeerruimte voor meerdere auto's.",
    glance: {
      eyebrow: "In één oogopslag",
      tiles: [
        { title: "Tot 8 personen", sub: "4 slaapkamers" },
        { title: "2 badkamers", sub: "1 met bad, douche & wc · 1 met inloopdouche & wc" },
        { title: "Privé zwembad", sub: "8m op 4 m · diepte 1–2 m · instaptrap · volledige privacy" },
        { title: "Babypakket beschikbaar", sub: "Reisbedje + kinderstoel + linnen · 0–24 maanden · max. 2 baby's" },
        { title: "Airco", sub: "Warm & koud in woonkamer en alle slaapkamers" },
        { title: "Was- en droogmachine", sub: "In aparte bijkeuken" },
        { title: "Gratis wifi", sub: "Glasvezelnetwerk" },
        { title: "Privéparkeerplaats", sub: "Meerdere auto's · omheind terrein · elektrische poort · 1 carport" },
        { title: "Check-in vanaf 16:00", sub: "Check-out voor 10:00" },
        { title: "Vliegvelden in de buurt", sub: "Alicante 59 km (±35 min) · Valencia 98 km (±60 min)" },
      ],
      rulesTitle: "Huisregels",
      rules: ["Niet roken", "Geen huisdieren", "Geen feesten of luide muziek"],
    },
    featuresEyebrow: "Wat u krijgt", featuresTitle: "Alles voor een zorgeloos verblijf.",
    features: [
      { title: "Privézwembad 8m op 4m", body: "De hele dag in de zon, met instaptrap, omgeven door een beschut terras met ligbedden en parasols." },
      { title: "Grote buitenkeuken", body: "De favoriete plek van onze gasten, 4-pits gasvuur, koel-vriescombinatie, vaatwasser en een lange eettafel." },
      { title: "4 slaapkamers · 8 personen", body: "Kingsize, tweepersoons en twee twin-kamers. Twee recent gerenoveerde badkamers. Airco in elke kamer." },
      { title: "1000 m² omheind terrein", body: "Elektrische poort, parking voor meerdere wagens, omsloten tuin, volledige privacy voor familie en vrienden." },
      { title: "5 km van Benidorm", body: "La Nucia, Costa Blanca. Winkels en restaurants op wandelafstand, snelweg op 3 km." },
      { title: "Comfort in elk seizoen", body: "Vaste warm/koud airconditioning in slaapkamers en woonkamer, plus gas-centrale verwarming voor winterverblijven." },
    ],
    testimonialsEyebrow: "Gastenbeoordelingen",
    testimonialsTitle: "Wat gasten zeggen na hun verblijf",
    testimonialsLead: "Een paar woorden van families die hun vakantie in Casa Mi Pajobe doorbrachten.",
    testimonialsStayed: "Verbleef",
    galleryEyebrow: "Foto's", galleryTitle: "Ontdek de villa",
    galleryLead: "Bekijk Casa Mi Pajobe van dichtbij, vier lichte slaapkamers, twee gerenoveerde badkamers, een ruime woonkamer en een mediterrane tuin gemaakt voor zorgeloze dagen.",
    locationEyebrow: "Ligging", locationTitle: "La Nucia, Costa Blanca",
    locationLead: "De villa ligt in de rustige urbanisatie Panorama, met uitzicht op de bergen en de skyline van Benidorm. Winkels en restaurants op wandelafstand, snelweg op 3 km.",
    locationItems: [
      ["Benidorm & strand", "5 km"], ["Albir", "10 min"], ["Shopping La Marina", "5 km"],
      ["Luchthaven Alicante", "59 km"], ["Luchthaven Valencia", "98 km"], ["Snelwegafrit", "3 km"],
    ],
    surroundingsEyebrow: "In de omgeving",
    surroundingsTitle: "Omgeving & activiteiten",
    surroundingsBody: [
      "Casa Mi Pajobe ligt in La Nucia, een rustig heuveldorp dat zijn Spaanse karakter bewaard heeft terwijl Benidorm op amper 5 km ligt. U geniet 's avonds van de stilte van de bergen en duikt overdag zo de drukte van de kust in, zonder ooit ver te moeten rijden.",
      "De dichtstbijzijnde stranden liggen op een korte rit de heuvel af. Playa del Albir heeft een kalme kiezelbaai met een autovrije boulevard, terwijl Playa de Poniente en Levante in Benidorm brede zandstranden, strandbars en watersport bieden. Wie met kinderen naar de Costa Blanca komt, vindt hier veel in de buurt: Terra Mítica, Aqualandia, Mundomar en dierenpark Terra Natura liggen allemaal binnen twintig minuten, en de sportstad van La Nucia zelf heeft zwembaden, tennisvelden en een skatepark.",
      "Dagtrips zijn vanaf hier eenvoudig. Wandel de Bernia-kam of de watervallen van Les Fonts de l'Algar, rijd omhoog naar het oude dorpje Guadalest boven zijn turkooizen stuwmeer, of trek noordwaarts naar de witte straatjes van Altea en de Peñón de Ifach in Calpe. Alicante en Valencia zijn allebei goed te doen voor een stadsdag.",
      "Dichter bij huis heeft La Nucia een wekelijkse markt, een mooi aanbod tapasbars en familierestaurants, en supermarkten op wandelafstand van de villa, zodat u de meeste avonden gewoon de buitenkeuken aansteekt en bij het zwembad blijft.",
    ],
    pricingEyebrow: "Reserveren", pricingTitle: "Verblijf & tarieven",
    pricingLeft: [
      "Minimumverblijf: 7 nachten",
      "Aankomst/vertrek elke dag, juli & augustus zaterdag tot zaterdag",
      "Check-in 16–20 u · Check-out 8–10 u",
      "Voorschot bij reservatie: 25% van de huurprijs",
      "Saldo, linnen, schoonmaak & waarborg: 8 weken voor aankomst",
    ],
    pricingRight: [
      "Rookvrije villa", "Geen huisdieren",
      "Enkel toeristische verhuur, geen jongerengroepen, geen feesten",
    ],
    ratesIntro: "Weektarieven in EUR. Boek minder slaapkamers en betaal minder, de rest van de villa blijft volledig voor u.",
    ratesColPeriod: "Periode",
    ratesPerWeek: "/ week",
    ratesBedroomsLabel: (n) => `${n} slaapkamers`,
    ratesNotAvailable: "-",
    ratesIncludedTitle: "Inbegrepen",
    ratesNotIncludedTitle: "Niet inbegrepen",
    ratesIncluded: [
      "Check-in / check-out door onze sleutelbeheerder",
      "Bedden opgemaakt bij aankomst",
      "Wifi en TV (Vlaanderen)",
      "Waterverbruik (afhankelijk van periode & bezetting, extra €4,00/m³)",
    ],
    ratesNotIncluded: [
      "Schoonmaak + wasservice linnen: €230 per verblijf",
      "Bed-, bad- & keukenlinnen: €10 per persoon/verblijf",
      "Waarborg: €500 (terugbetaald na verblijf)",
      "Babypakket (0–24 maanden): €30 per verblijf",
      "Elektriciteit: €0,40 / kWh (wordt afgehouden van de waarborg)",
      "Gas in laagseizoen (wordt afgehouden van de waarborg)",
    ],
    ratesStayInfoTitle: "Boekingsvoorwaarden",
    ratesHouseRulesTitle: "Huisregels",
    faqEyebrow: "FAQ",
    faqTitle: "Veelgestelde vragen",
    faqLead: "Snelle antwoorden over inchecken, uitchecken, annuleringsbeleid en huisdieren.",
    faq: [
      { q: "Hoe laat is de check-in?", a: "Inchecken kan tussen 16.00 en 20.00 uur. Onze sleutelbeheerder ontvangt u aan de villa, overhandigt de sleutels en geeft u een rondleiding. Komt uw vlucht buiten deze uren aan, laat het ons dan op voorhand weten en we kijken wat mogelijk is." },
      { q: "Hoe laat is de check-out?", a: "Uitchecken gebeurt tussen 8.00 en 10.00 uur op de dag van vertrek, zodat de villa kan worden schoongemaakt en klaargemaakt voor de volgende gasten." },
      { q: "Wat is het annuleringsbeleid?", a: "Het voorschot van 25% bij reservering wordt niet terugbetaald. Het saldo is verschuldigd 8 weken voor aankomst; annuleringen na die datum worden niet terugbetaald. Wij raden sterk een reisverzekering met annuleringsdekking aan." },
      { q: "Zijn huisdieren toegestaan?", a: "Nee, huisdieren zijn niet toegelaten in Casa Mi Pajobe. De villa is binnen ook strikt rookvrij." },
      { q: "Is linnengoed inbegrepen?", a: "Bed-, bad- en keukenlinnen is beschikbaar voor €10 per persoon per verblijf. Handdoeken zijn inbegrepen in de saldobetaling." },
      { q: "Is er wifi?", a: "Ja, snelle wifi is beschikbaar in de hele villa en tuin." },
      { q: "Kunnen we een baby of peuter meenemen?", a: "Ja. Een reisbedje en kinderstoel zijn beschikbaar voor €30 (inbegrepen in het babypakket op het aanvraagformulier)." },
      { q: "Is het zwembad verwarmd?", a: "Het zwembad is niet verwarmd, maar krijgt de hele dag directe zon en is comfortabel warm van mei tot en met oktober." },
      { q: "Wat is de dichtstbijzijnde supermarkt?", a: "Op wandelafstand in La Nucia ligt een Mercadona-supermarkt, ongeveer 10 minuten te voet." },
      { q: "Is er parking aan de villa?", a: "Ja, het omsloten terrein heeft parkeerplaats voor meerdere auto's achter de elektrische poort." },
    ],
    ctaTitle: "Klaar om uw verblijf te plannen?",
    ctaBody: "Stuur een korte aanvraag, we antwoorden binnen 24 uur.",
    ctaButton: "Start uw aanvraag",
    footerExplore: "Ontdek", footerContact: "Contact",
    footerContactBody: (link) => <>Voor elke vraag, prijsopgave of reservering, gebruik ons {link}.</>,
    footerLicense: "Verhuur licentie\u00a0 VT-454687-A\u00a0\nVERGUNNING KORTE TERMIJN VERHUUR:\u00a0ESFCTU00000305300037183700000000000000000VT-454687-A3\u00a0\nVERGUNNING LANGE TERMIJN VERHUUR: ESFCNT000003053000371837000000000000000000000000009", changeLanguage: "Andere taal",
  },
  fr: {
    navVilla: "La villa", navGallery: "Galerie", navReviews: "Avis", navLocation: "Situation", navRates: "Tarifs", navGuide: "Guide", navFaq: "FAQ",
    bookCta: "Réserver", heroEyebrow: "LA NUCIA · COSTA BLANCA ·\nVT-454687-A\u00a0\n",
    heroSub: "Une villa indépendante avec piscine privée, grande cuisine extérieure et jardin méditerranéen clos. Vos vacances de rêve sur la Costa Blanca commencent ici.",
    heroPrimary: "Réserver", heroSecondary: "Découvrir la villa",
    heroQuote: "Pour la troisième année consécutive, nous avons séjourné un mois entier. Chaque année, nous constatons les améliorations apportées par les propriétaires.",
    heroQuoteAttribution: "Dirk & Brigitte, Belgique · Spain-Holiday",
    aboutEyebrow: "La villa", aboutTitle: "Calme, privée, faite pour de longues journées de farniente.",
    aboutP1: "Casa Mi Pajobe est une villa indépendante sur un terrain clos de 1000 m² dans l'urbanisation Panorama de La Nucia, à seulement 5 km de Benidorm et des plages de la Costa Blanca.",
    aboutP2: "Quatre chambres, deux salles de bains rénovées, un salon lumineux ouvrant sur la véranda et à l'extérieur, une piscine privée de 8x4m avec escalier romain, un salon et la cuisine extérieure entièrement équipée que nos hôtes adorent.",
    aboutP3: "Vous êtes toujours le seul locataire. Le terrain est entièrement clôturé et fermé par un portail électrique, avec parking pour plusieurs voitures.",
    glance: {
      eyebrow: "En un coup d'œil",
      tiles: [
        { title: "Jusqu'à 8 personnes", sub: "4 chambres" },
        { title: "2 salles de bain", sub: "1 avec baignoire, douche & WC · 1 avec douche à l'italienne & WC" },
        { title: "Piscine privée", sub: "8 × 4 m · profondeur 1–2 m · escalier romain · intimité totale" },
        { title: "Pack bébé disponible", sub: "Lit parapluie + chaise haute + linge · 0–24 mois · max. 2 bébés" },
        { title: "Climatisation", sub: "Chaud & froid dans le salon et toutes les chambres" },
        { title: "Lave-linge & sèche-linge", sub: "Dans une buanderie séparée" },
        { title: "Wi-Fi gratuit", sub: "Réseau fibre optique" },
        { title: "Parking privé", sub: "Plusieurs voitures · terrain clos · portail électrique · 1 carport" },
        { title: "Arrivée dès 16h00", sub: "Départ avant 10h00" },
        { title: "Aéroports à proximité", sub: "Alicante 59 km (±35 min) · Valencia 98 km (±60 min)" },
      ],
      rulesTitle: "Règlement intérieur",
      rules: ["Non-fumeur", "Animaux non admis", "Pas de fêtes ni musique forte"],
    },
    featuresEyebrow: "À l'intérieur", featuresTitle: "Tout pour un séjour facile.",
    features: [
      { title: "Piscine privée 8x4m", body: "Ensoleillée toute la journée, escalier d'accès, entourée d'une terrasse abritée avec transats et parasols." },
      { title: "Grande\u00a0cuisine extérieure", body: "L'endroit préféré de nos hôtes, gazinière 4 feux, réfrigérateur/congélateur, lave-vaisselle et grande table à manger." },
      { title: "4 chambres · 8 personnes", body: "Lit king-size, double et deux chambres à lits jumeaux. Deux salles de bains récemment rénovées. Climatisation partout." },
      { title: "Terrain clos de 1000 m²", body: "Portail électrique, parking pour plusieurs voitures, jardin clos, intimité totale pour familles et amis." },
      { title: "À 5 km des plages de Benidorm", body: "La Nucia, Costa Blanca. Commerces et restaurants à pied, sortie d'autoroute à 3 km." },
      { title: "Confort toute l'année", body: "Climatisation réversible fixe dans les chambres et le salon, plus chauffage central au gaz pour les séjours d'hiver." },
    ],
    testimonialsEyebrow: "Avis des hôtes",
    testimonialsTitle: "Ce que disent les hôtes après leur séjour",
    testimonialsLead: "Quelques mots de familles qui ont passé leurs vacances à Casa Mi Pajobe.",
    testimonialsStayed: "Séjour",
    galleryEyebrow: "Galerie", galleryTitle: "Découvrez la villa",
    galleryLead: "Découvrez Casa Mi Pajobe de plus près, quatre chambres lumineuses, deux salles de bain rénovées, un grand salon et un jardin méditerranéen pensé pour des journées paisibles.",
    locationEyebrow: "Situation", locationTitle: "La Nucia, Costa Blanca",
    locationLead: "La villa se trouve dans l'urbanisation calme de Panorama, avec vue sur les montagnes et la skyline de Benidorm. Commerces et restaurants à pied, sortie d'autoroute à 3 km.",
    locationItems: [
      ["Benidorm & plage", "5 km"], ["Albir", "10 min"], ["Centre commercial La Marina", "5 km"],
      ["Aéroport d'Alicante", "59 km"], ["Aéroport de Valence", "98 km"], ["Sortie d'autoroute", "3 km"],
    ],
    surroundingsEyebrow: "Aux alentours",
    surroundingsTitle: "Environs & activités",
    surroundingsBody: [
      "Casa Mi Pajobe se trouve à La Nucia, un village de collines paisible qui a gardé son caractère espagnol alors que Benidorm n'est qu'à 5 km. Vous profitez du calme de la montagne le soir et de l'animation de la côte dès que l'envie vous prend, sans jamais faire un long trajet.",
      "Les plages les plus proches sont à quelques minutes en descendant la colline. Playa del Albir offre une baie de galets tranquille et une promenade sans voitures, tandis que les plages de Poniente et Levante à Benidorm proposent de grandes étendues de sable, des bars de plage et des sports nautiques. Pour un séjour sur la Costa Blanca avec des enfants, tout est proche : Terra Mítica, Aqualandia, Mundomar et le parc animalier Terra Natura sont à moins de vingt minutes, et la cité sportive de La Nucia dispose de piscines, de courts de tennis et d'un skatepark.",
      "Les excursions à la journée sont faciles d'ici. Randonnez sur la crête de Bernia ou aux cascades des Fonts de l'Algar, montez jusqu'au vieux village de Guadalest au-dessus de son lac turquoise, ou partez vers le nord découvrir les ruelles blanches d'Altea et le Peñón de Ifach à Calpe. Alicante et Valence se rejoignent aisément pour une journée en ville.",
      "Plus près, La Nucia a son marché hebdomadaire, un bel éventail de bars à tapas et de restaurants familiaux, ainsi que des supermarchés à distance de marche de la villa : la plupart des soirs, vous allumerez simplement la cuisine extérieure et resterez près de la piscine.",
    ],
    pricingEyebrow: "Réservation", pricingTitle: "Séjour & tarifs",
    pricingLeft: [
      "Séjour minimum : 7 nuits",
      "Arrivée/départ tous les jours, juillet & août du samedi au samedi",
      "Arrivée 16h–20h · Départ 8h–10h",
      "Acompte à la réservation : 25% du prix de location",
      "Solde, linge, ménage & caution : 8 semaines avant l'arrivée",
    ],
    pricingRight: [
      "Villa non-fumeur", "Animaux non admis",
      "Location touristique uniquement, pas de groupes de jeunes, pas de fêtes",
    ],
    ratesIntro: "Tarifs hebdomadaires en EUR. Réservez moins de chambres et payez moins, le reste de la villa reste entièrement à vous.",
    ratesColPeriod: "Période",
    ratesPerWeek: "/ semaine",
    ratesBedroomsLabel: (n) => `${n} chambres`,
    ratesNotAvailable: "-",
    ratesIncludedTitle: "Inclus",
    ratesNotIncludedTitle: "Non inclus",
    ratesIncluded: [
      "Check-in / check-out par notre régisseur",
      "Lits faits à l'arrivée",
      "Wi-Fi et TV (Flandre)",
      "Consommation d'eau (selon période & occupation, supplément €4,00/m³)",
    ],
    ratesNotIncluded: [
      "Ménage + lavage du linge : €230 par séjour",
      "Linge de lit, bain & cuisine : €10 par personne/séjour",
      "Caution : €500 (remboursée après le séjour)",
      "Forfait bébé (0–24 mois) : €30 par séjour",
      "Électricité : €0,40 / kWh (déduit de la caution)",
      "Gaz en basse saison (déduit de la caution)",
    ],
    ratesStayInfoTitle: "Conditions de réservation",
    ratesHouseRulesTitle: "Règlement intérieur",
    faqEyebrow: "FAQ",
    faqTitle: "Questions fréquentes",
    faqLead: "Réponses rapides sur l'arrivée, le départ, l'annulation et les animaux.",
    faq: [
      { q: "À quelle heure se fait l'arrivée ?", a: "L'arrivée se fait entre 16h et 20h. Notre régisseur vous accueille à la villa, vous remet les clés et vous fait visiter. Si votre vol arrive en dehors de ces horaires, prévenez-nous à l'avance et nous ferons de notre mieux pour nous adapter." },
      { q: "À quelle heure se fait le départ ?", a: "Le départ se fait entre 8h et 10h le jour du départ, afin que la villa puisse être nettoyée et préparée pour les hôtes suivants." },
      { q: "Quelle est la politique d'annulation ?", a: "L'acompte de 25% versé à la réservation n'est pas remboursable. Le solde est dû 8 semaines avant l'arrivée ; les annulations après cette date ne sont pas remboursées. Nous recommandons vivement une assurance voyage avec garantie annulation." },
      { q: "Les animaux sont-ils acceptés ?", a: "Non, les animaux ne sont pas admis à Casa Mi Pajobe. La villa est également strictement non-fumeur à l'intérieur." },
      { q: "Le linge est-il inclus ?", a: "Le linge de lit, de bain et de cuisine est disponible pour 10 € par personne et par séjour. Les serviettes sont incluses dans le solde." },
      { q: "Y a-t-il du Wi-Fi ?", a: "Oui, un Wi-Fi rapide est disponible dans toute la villa et le jardin." },
      { q: "Pouvons-nous venir avec un bébé ou un jeune enfant ?", a: "Oui. Un lit parapluie et une chaise haute sont disponibles pour 30 € (inclus dans le pack bébé du formulaire de réservation)." },
      { q: "La piscine est-elle chauffée ?", a: "La piscine n'est pas chauffée, mais elle est ensoleillée toute la journée et reste agréablement chaude de mai à octobre." },
      { q: "Où est le supermarché le plus proche ?", a: "Un supermarché Mercadona se trouve à distance de marche à La Nucia, à environ 10 minutes à pied." },
      { q: "Y a-t-il un parking à la villa ?", a: "Oui, le terrain clos dispose d'un parking pour plusieurs voitures derrière le portail électrique." },
    ],
    ctaTitle: "Prêt à planifier votre séjour ?",
    ctaBody: "Envoyez une demande rapide, nous répondons sous 24 heures.",
    ctaButton: "Démarrer ma demande",
    footerExplore: "Explorer", footerContact: "Contact",
    footerContactBody: (link) => <>Pour toute question, devis ou réservation, utilisez notre {link}.</>,
    footerLicense: "Verhuur licentie\u00a0 VT-454687-A\u00a0\nVERGUNNING KORTE TERMIJN VERHUUR:\u00a0ESFCTU00000305300037183700000000000000000VT-454687-A3\u00a0\nVERGUNNING LANGE TERMIJN VERHUUR: ESFCNT000003053000371837000000000000000000000000009", changeLanguage: "Changer de langue",
  },
  de: {
    navVilla: "Die Villa", navGallery: "Galerie", navReviews: "Bewertungen", navLocation: "Lage", navRates: "Preise", navGuide: "Reiseführer", navFaq: "FAQ",
    bookCta: "Buchen", heroEyebrow: "LA NUCIA · COSTA BLANCA ·\nVT-454687-A\u00a0\n",
    heroSub: "Eine freistehende Villa mit privatem Pool, großer Außenküche und eingezäuntem mediterranen Garten. Ihr Traumurlaub an der Costa Blanca beginnt hier.",
    heroPrimary: "Jetzt buchen", heroSecondary: "Villa ansehen",
    heroQuote: "Zum dritten Mal in Folge haben wir einen ganzen Monat verbracht. Jedes Jahr sehen wir Verbesserungen, die die Eigentümer vorgenommen haben.",
    heroQuoteAttribution: "Dirk & Brigitte, Belgien · Spain-Holiday",
    aboutEyebrow: "Die Villa", aboutTitle: "Ruhig, privat und gemacht für lange, faule Tage.",
    aboutP1: "Casa Mi Pajobe ist eine freistehende Villa auf 1000 m² umzäuntem Grundstück in der Urbanisation Panorama in La Nucia, nur 5 km von Benidorm und den Stränden der Costa Blanca entfernt.",
    aboutP2: "Vier Schlafzimmer, zwei renovierte Bäder, ein helles Wohnzimmer mit Zugang zum Wintergarten und draußen ein privater 8x4m Pool mit Einstiegstreppe, eine Lounge und die voll ausgestattete Außenküche, in die sich unsere Gäste verlieben.",
    aboutP3: "Sie sind immer der einzige Mieter. Das gesamte Grundstück ist eingezäunt und mit einem elektrischen Tor verschlossen, mit Parkplatz für mehrere Autos.",
    glance: {
      eyebrow: "Auf einen Blick",
      tiles: [
        { title: "Bis zu 8 Personen", sub: "4 Schlafzimmer" },
        { title: "2 Badezimmer", sub: "1 mit Wanne, Dusche & WC · 1 mit ebenerdiger Dusche & WC" },
        { title: "Privater Pool", sub: "8 × 4 m · Tiefe 1–2 m · Einstiegstreppe · volle Privatsphäre" },
        { title: "Babypaket verfügbar", sub: "Reisebett + Hochstuhl + Wäsche · 0–24 Monate · max. 2 Babys" },
        { title: "Klimaanlage", sub: "Warm & kalt im Wohnzimmer und allen Schlafzimmern" },
        { title: "Waschmaschine & Trockner", sub: "Im separaten Hauswirtschaftsraum" },
        { title: "Kostenloses WLAN", sub: "Glasfasernetz" },
        { title: "Privatparkplatz", sub: "Mehrere Autos · umzäuntes Grundstück · elektrisches Tor · 1 Carport" },
        { title: "Check-in ab 16:00", sub: "Check-out bis 10:00" },
        { title: "Flughäfen in der Nähe", sub: "Alicante 59 km (±35 Min.) · Valencia 98 km (±60 Min.)" },
      ],
      rulesTitle: "Hausregeln",
      rules: ["Nichtraucher", "Keine Haustiere", "Keine Partys oder laute Musik"],
    },
    featuresEyebrow: "Was Sie erwartet", featuresTitle: "Alles für einen entspannten Aufenthalt.",
    features: [
      { title: "Privater Pool 8x4m", body: "Ganztägig sonnig, mit Einstiegstreppe, umgeben von einer geschützten Terrasse mit Sonnenliegen und Sonnenschirmen." },
      { title: "Große Außenküche", body: "Der Lieblingsplatz unserer Gäste, 4-Flammen-Gasherd, Kühl-/Gefrierkombi, Spülmaschine und langer Esstisch." },
      { title: "4 Schlafzimmer · 8 Personen", body: "Kingsize, Doppel- und zwei Twin-Zimmer. Zwei kürzlich renovierte Bäder. Klimaanlage überall." },
      { title: "1000 m² eingezäunt", body: "Elektrisches Tor, Parkplatz für mehrere Autos, umzäunter Garten, volle Privatsphäre für Familie und Freunde." },
      { title: "5 km zu den Stränden Benidorms", body: "La Nucia, Costa Blanca. Geschäfte und Restaurants zu Fuß erreichbar, Autobahnausfahrt 3 km." },
      { title: "Komfort zu jeder Jahreszeit", body: "Fest installierte Warm-/Kalt-Klimaanlage in Schlafzimmern und Wohnzimmer, plus Gaszentralheizung für Winteraufenthalte." },
    ],
    testimonialsEyebrow: "Gästebewertungen",
    testimonialsTitle: "Was Gäste nach ihrem Aufenthalt sagen",
    testimonialsLead: "Ein paar Worte von Familien, die ihren Urlaub in Casa Mi Pajobe verbracht haben.",
    testimonialsStayed: "Aufenthalt",
    galleryEyebrow: "Galerie", galleryTitle: "Entdecken Sie die Villa",
    galleryLead: "Werfen Sie einen näheren Blick auf Casa Mi Pajobe, vier helle Schlafzimmer, zwei renovierte Badezimmer, ein großzügiges Wohnzimmer und ein mediterraner Garten für entspannte Tage.",
    locationEyebrow: "Lage", locationTitle: "La Nucia, Costa Blanca",
    locationLead: "Die Villa liegt in der ruhigen Urbanisation Panorama, mit Blick auf die Berge und die Skyline von Benidorm. Geschäfte und Restaurants zu Fuß, Autobahnausfahrt 3 km.",
    locationItems: [
      ["Benidorm & Strand", "5 km"], ["Albir", "10 Min."], ["Einkaufszentrum La Marina", "5 km"],
      ["Flughafen Alicante", "59 km"], ["Flughafen Valencia", "98 km"], ["Autobahnausfahrt", "3 km"],
    ],
    surroundingsEyebrow: "In der Umgebung",
    surroundingsTitle: "Umgebung & Aktivitäten",
    surroundingsBody: [
      "Casa Mi Pajobe liegt in La Nucia, einem ruhigen Hügeldorf, das seinen spanischen Charakter bewahrt hat, während Benidorm nur 5 km entfernt ist. Abends genießen Sie die Stille der Berge, tagsüber sind Sie im Handumdrehen an der lebhaften Küste, ganz ohne lange Fahrten.",
      "Die nächsten Strände erreichen Sie in wenigen Minuten bergab. Playa del Albir bietet eine ruhige Kiesbucht mit autofreier Promenade, Playa de Poniente und Levante in Benidorm breite Sandstrände, Strandbars und Wassersport. Wer mit Kindern an die Costa Blanca reist, findet vieles in der Nähe: Terra Mítica, Aqualandia, Mundomar und der Tierpark Terra Natura liegen alle innerhalb von zwanzig Minuten, und La Nucias eigene Sportstadt hat Schwimmbäder, Tennisplätze und einen Skatepark.",
      "Tagesausflüge sind von hier aus unkompliziert. Wandern Sie über den Bernia-Grat oder zu den Wasserfällen der Fonts de l'Algar, fahren Sie hinauf ins alte Bergdorf Guadalest über seinem türkisfarbenen Stausee, oder nordwärts in die weißen Gassen von Altea und zum Peñón de Ifach in Calpe. Alicante und Valencia sind für einen Stadttag gut erreichbar.",
      "Ganz in der Nähe hat La Nucia einen Wochenmarkt, viele Tapasbars und Familienrestaurants sowie Supermärkte in Gehweite der Villa, sodass Sie an den meisten Abenden einfach die Außenküche anwerfen und am Pool bleiben.",
    ],
    pricingEyebrow: "Buchung", pricingTitle: "Aufenthalt & Preise",
    pricingLeft: [
      "Mindestaufenthalt: 7 Nächte",
      "An-/Abreise täglich, Juli & August Samstag zu Samstag",
      "Check-in 16–20 Uhr · Check-out 8–10 Uhr",
      "Anzahlung bei Reservierung: 25% des Mietpreises",
      "Restbetrag, Wäsche, Reinigung & Kaution: 8 Wochen vor Anreise",
    ],
    pricingRight: [
      "Nichtraucher-Villa", "Keine Haustiere",
      "Nur touristische Vermietung, keine Jugendgruppen, keine Partys",
    ],
    ratesIntro: "Wochenpreise in EUR. Buchen Sie weniger Schlafzimmer und zahlen Sie weniger, der Rest der Villa gehört vollständig Ihnen.",
    ratesColPeriod: "Zeitraum",
    ratesPerWeek: "/ Woche",
    ratesBedroomsLabel: (n) => `${n} Schlafzimmer`,
    ratesNotAvailable: "-",
    ratesIncludedTitle: "Inbegriffen",
    ratesNotIncludedTitle: "Nicht inbegriffen",
    ratesIncluded: [
      "Check-in / Check-out durch unseren Schlüsselverwalter",
      "Betten bei Anreise bezogen",
      "WLAN und TV (Flandern)",
      "Wasserverbrauch (je nach Zeitraum & Belegung, Aufpreis €4,00/m³)",
    ],
    ratesNotIncluded: [
      "Reinigung + Wäsche: €230 pro Aufenthalt",
      "Bett-, Bad- & Küchenwäsche: €10 pro Person/Aufenthalt",
      "Kaution: €500 (nach dem Aufenthalt rückerstattet)",
      "Baby-Paket (0–24 Monate): €30 pro Aufenthalt",
      "Strom: €0,40 / kWh (wird von der Kaution abgezogen)",
      "Gas in der Nebensaison (wird von der Kaution abgezogen)",
    ],
    ratesStayInfoTitle: "Buchungsbedingungen",
    ratesHouseRulesTitle: "Hausregeln",
    faqEyebrow: "FAQ",
    faqTitle: "Häufig gestellte Fragen",
    faqLead: "Schnelle Antworten zu Check-in, Check-out, Stornierung und Haustieren.",
    faq: [
      { q: "Wann ist Check-in?", a: "Check-in ist zwischen 16 und 20 Uhr. Unser Schlüsselverwalter empfängt Sie an der Villa, übergibt die Schlüssel und zeigt Ihnen alles. Wenn Ihr Flug außerhalb dieser Zeiten ankommt, sagen Sie uns vorab Bescheid, wir tun unser Bestes." },
      { q: "Wann ist Check-out?", a: "Check-out ist zwischen 8 und 10 Uhr am Abreisetag, damit die Villa für die nächsten Gäste gereinigt und vorbereitet werden kann." },
      { q: "Wie sind die Stornierungsbedingungen?", a: "Die bei Buchung gezahlte Anzahlung von 25% ist nicht erstattungsfähig. Der Restbetrag ist 8 Wochen vor Anreise fällig; Stornierungen nach diesem Datum werden nicht erstattet. Wir empfehlen dringend eine Reiseversicherung mit Stornoschutz." },
      { q: "Sind Haustiere erlaubt?", a: "Nein, Haustiere sind in Casa Mi Pajobe nicht erlaubt. Die Villa ist innen außerdem strikt Nichtraucher." },
      { q: "Ist Bettwäsche inklusive?", a: "Bett-, Bade- und Küchenwäsche ist für 10 € pro Person und Aufenthalt verfügbar. Handtücher sind in der Restzahlung enthalten." },
      { q: "Gibt es WLAN?", a: "Ja, schnelles WLAN ist in der gesamten Villa und im Garten verfügbar." },
      { q: "Können wir ein Baby oder Kleinkind mitbringen?", a: "Ja. Reisebett und Hochstuhl sind für 30 € verfügbar (im Baby-Paket auf dem Buchungsformular enthalten)." },
      { q: "Ist der Pool beheizt?", a: "Der Pool ist nicht beheizt, bekommt aber den ganzen Tag direkte Sonne und ist von Mai bis Oktober angenehm warm." },
      { q: "Wo ist der nächste Supermarkt?", a: "In La Nucia befindet sich in Gehweite ein Mercadona-Supermarkt, etwa 10 Minuten zu Fuß." },
      { q: "Gibt es Parkplätze an der Villa?", a: "Ja, das umzäunte Grundstück bietet hinter dem elektrischen Tor Parkplätze für mehrere Autos." },
    ],
    ctaTitle: "Bereit, Ihren Aufenthalt zu planen?",
    ctaBody: "Senden Sie eine kurze Anfrage, wir antworten innerhalb von 24 Stunden.",
    ctaButton: "Anfrage starten",
    footerExplore: "Entdecken", footerContact: "Kontakt",
    footerContactBody: (link) => <>Für Fragen, Angebote oder Reservierungen nutzen Sie bitte unser {link}.</>,
    footerLicense: "Verhuur licentie\u00a0 VT-454687-A\u00a0\nVERGUNNING KORTE TERMIJN VERHUUR:\u00a0ESFCTU00000305300037183700000000000000000VT-454687-A3\u00a0\nVERGUNNING LANGE TERMIJN VERHUUR: ESFCNT000003053000371837000000000000000000000000009", changeLanguage: "Sprache ändern",
  },
};

export function isLang(value: string): value is Lang {
  return value === "en" || value === "nl" || value === "fr" || value === "de";
}